import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { useCustomer, useCustomerStore } from "hooks";
import {
  CUSTOMER_OBJECT_TYPES,
  CUSTOMER_TYPES,
  Customer,
  CustomerForm,
  CustomerObjectType,
  CustomerQuery,
  CustomerType,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type {
  ColumnFilterConfig,
  QueryState,
} from "components/ui-common/AgGridTable";
import { Plus } from "lucide-react";

const DEFAULT_OBJECT_TYPE: CustomerObjectType = "personal";
const DEFAULT_CUSTOMER_TYPE: CustomerType = "customer";

const defaultFormValues: CustomerForm = {
  code: "",
  name: "",
  phone: "",
  email: "",
  address: "",
  object_type: DEFAULT_OBJECT_TYPE,
  type: DEFAULT_CUSTOMER_TYPE,
};

const objectTypeOptions: { value: CustomerObjectType; label: string }[] = [
  { value: "personal", label: "Cá nhân" },
  { value: "company", label: "Doanh nghiệp" },
];

const customerTypeOptions: { value: CustomerType; label: string }[] = [
  { value: "both", label: "Tất cả" },
  { value: "customer", label: "Khách hàng" },
  { value: "supplier", label: "Nhà cung cấp" },
];

const getOptionLabel = <T extends string>(
  options: { value: T; label: string }[],
  value: unknown,
) => options.find((opt) => opt.value === value)?.label ?? String(value ?? "");

const normalizeSelectValue = <T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T => {
  if (typeof value === "string") {
    const normalized = value.trim() as T;
    if (allowed.includes(normalized)) {
      return normalized;
    }
  }
  return fallback;
};

const Customers: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<Customer | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<CustomerQuery>({
    page: 1,
    pageSize: 20,
  });
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>('');
  const [confirmRow, setConfirmRow] = useState<Customer | null>(null);
  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useCustomerStore();
  const { fetchList, createCustomer, updateCustomer, deleteCustomer } =
    useCustomer();

  const {
    data: customerData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomerForm>({
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (customerData?.data) {
      setRows(customerData.data as Customer[]);
    }
  }, [customerData, setRows, isFetching]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createCustomer.isPending ||
        updateCustomer.isPending ||
        deleteCustomer.isPending,
      ),
    );
  }, [
    isFetching,
    createCustomer.isPending,
    updateCustomer.isPending,
    deleteCustomer.isPending,
    setLoading,
  ]);

  const handleDeleteRow = useCallback((row: Customer) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteCustomer.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
          reset(defaultFormValues);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa khách hàng không thành công");
      },
    });
  }, [confirmRow, deleteCustomer, editingId, removeRow, reset, setEditingId]);

  const columns = useMemo<ColDef<Customer>[]>(() => {
    return [
      {
        headerName: "Mã KH",
        field: "code",
        minWidth: 140,

      },
      {
        headerName: "Tên khách hàng",
        field: "name",
        flex: 1,
        minWidth: 180,

      },
      {
        headerName: "Đối tượng",
        field: "object_type",
        minWidth: 140,

        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: objectTypeOptions.map((item) => item.value),
          formatValue: (value: CustomerObjectType) =>
            getOptionLabel(objectTypeOptions, value),
        },
        valueFormatter: (params) =>
          getOptionLabel(objectTypeOptions, params.value),
      },
      {
        headerName: "Phân loại",
        field: "type",
        minWidth: 140,

        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: customerTypeOptions.map((item) => item.value),
          formatValue: (value: CustomerType) =>
            getOptionLabel(customerTypeOptions, value),
        },
        valueFormatter: (params) =>
          getOptionLabel(customerTypeOptions, params.value),
      },
      {
        headerName: "Số điện thoại",
        field: "phone",
        minWidth: 150,

      },
      {
        headerName: "Email",
        field: "email",
        minWidth: 180,

      },
      {
        headerName: "Địa chỉ",
        field: "address",
        flex: 1,
        minWidth: 200,

      },
    ];
  }, []);

  const normalizePayload = useCallback(
    (row: Partial<Customer>): CustomerForm => ({
      code: String(row.code ?? "").trim(),
      name: String(row.name ?? "").trim(),
      phone: String(row.phone ?? "").trim(),
      email: String(row.email ?? "").trim(),
      address: String(row.address ?? "").trim(),
      object_type: normalizeSelectValue(
        row.object_type,
        CUSTOMER_OBJECT_TYPES,
        DEFAULT_OBJECT_TYPE,
      ),
      type: normalizeSelectValue(
        row.type,
        CUSTOMER_TYPES,
        DEFAULT_CUSTOMER_TYPE,
      ),
    }),
    [],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      code: "",
      name: "",
      phone: "",
      email: "",
      address: "",
      object_type: DEFAULT_OBJECT_TYPE,
      type: DEFAULT_CUSTOMER_TYPE,
    } as Customer;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<Customer>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (
        !payload.code ||
        !payload.name ||
        !payload.object_type ||
        !payload.type
      ) {
        SnackbarUtils.error(
          "Code, Tên khách hàng, Loại đối tượng và Phân loại là bắt buộc",
        );
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createCustomer.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as Customer);
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm khách hàng không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được khách hàng cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updateCustomer.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as Customer);
            } else {
              upsertRow({ ...(row as Customer), ...payload });
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật khách hàng không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createCustomer,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updateCustomer,
      upsertRow,
    ],
  );

  const onSubmit = useCallback(
    (values: CustomerForm) => {
      persistRow(
        { ...(editingId ? { id: editingId } : {}), ...values },
        {
          refetch: true,
          onSuccess: () => {
            setEditingId(null);
            reset(defaultFormValues);
          },
        },
      );
    },
    [editingId, persistRow, reset, setEditingId],
  );

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    reset(defaultFormValues);
  }, [reset, setEditingId]);

  const handleSaveRow = useCallback(
    (row: Customer, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
      });
    },
    [persistRow],
  );

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow('edit')
      setEditingId(params.data.id);
      setTimeout(() => gridApiRef.current?.startEditingCell({
        rowIndex: params.node.rowIndex,
        colKey: "code",
      }), 300);
    },
    [setEditingId],
  );

  const toggleInlineAddRow = useCallback(() => {
    setShowInlineAddRow((prev) => {
      const next = !prev ? 'add' : '';
      if (next) {
        resetInlineAddRow();
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
      }
      return next;
    });
  }, [resetInlineAddRow]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const filtersConfig = useMemo<Record<string, ColumnFilterConfig>>(
    () => ({
      object_type: {
        type: "dropdown",
        options: objectTypeOptions,
        onChange: handleQueryChange,
      },
      type: {
        type: "dropdown",
        options: customerTypeOptions,
        onChange: handleQueryChange,
      },
    }),
    [handleQueryChange],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Quản lý khách hàng</h1>

      </div>

      <div className="grid md:grid-cols-3 gap-6 hidden">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white border border-gray-200 shadow-sm rounded-lg p-4 space-y-3 md:col-span-2"
        >
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Mã KH *
              </label>
              <input
                {...register("code", { required: "Bắt buộc nhập mã KH" })}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none ${errors.code ? "border-red-500" : "border-gray-200"
                  }`}
                placeholder="CUST001"
              />
              {errors.code && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.code.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Tên khách hàng *
              </label>
              <input
                {...register("name", { required: "Bắt buộc nhập tên" })}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none ${errors.name ? "border-red-500" : "border-gray-200"
                  }`}
                placeholder="Nguyen Van A"
              />
              {errors.name && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Đối tượng *
              </label>
              <select
                {...register("object_type", {
                  required: "Bắt buộc chọn loại đối tượng",
                })}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none ${errors.object_type ? "border-red-500" : "border-gray-200"
                  }`}
              >
                {objectTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.object_type && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.object_type.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Phân loại *
              </label>
              <select
                {...register("type", { required: "Bắt buộc chọn phân loại" })}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none ${errors.type ? "border-red-500" : "border-gray-200"
                  }`}
              >
                {customerTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.type && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.type.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Số điện thoại
              </label>
              <input
                {...register("phone")}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="0909123456"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Email
              </label>
              <input
                {...register("email")}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="customer@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Địa chỉ
              </label>
              <input
                {...register("address")}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="123 Lê Lợi, HCM"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {editingId ? "Cập nhật" : "Thêm khách hàng"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Hủy chỉnh sửa
              </button>
            )}
          </div>
        </form>
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-4 space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Hướng dẫn nhanh
          </h3>
          <ul className="list-disc pl-4 space-y-1 text-sm text-gray-600">
            <li>Tạo mới bằng form, các trường bắt buộc có dấu *.</li>
            <li>Nhấp đôi vào ô trong bảng để chỉnh sửa nhanh từng dòng.</li>
            <li>
              Xóa dòng sẽ không thể khôi phục, hãy xác nhận trước khi xóa.
            </li>
          </ul>
        </div>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách khách hàng
            </h2>
            <p className="text-xs text-gray-500">
              Sử dụng grid để lọc, sắp xếp và chỉnh sửa trực tiếp.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="text-green-700" />
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={customerData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          filtersConfig={filtersConfig}
          getRowId={(params) => String(params.data.id)}
          onAfterActions={() => {
            setShowInlineAddRow('');
            setEditingId(null)
          }}
          onQueryChange={handleQueryChange}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách khách hàng, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa khách hàng?"
        description={`Bạn chắc chắn muốn xóa khách hàng "${confirmRow?.name ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteCustomer.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default Customers;
